import { createRoot } from "react-dom/client";
import App from "./App";
import { IconStroke } from "./components/IconStroke";
import "./index.css";
import { registerServiceWorker } from "./lib/serviceWorkerRegistration";
import { captureInstallPrompt } from "./lib/pwa";
import { installBackGuard } from "./lib/backGuard";
import { initAnalytics } from "./lib/analytics";
import { installThemeColorSync } from "./lib/themeColor";
import { initWindowChrome } from "./lib/windowChrome";
import { applyShellAtBoot, watchHardwareKeyboard } from "./lib/shellFlat";
import { warmProjectImagesAtBoot } from "./lib/projectImages";
import { PURCHASE_HOME_PATH, consoleOriginFor, isBuyerPath, isPurchaseHost } from "@shared/purchaseHost";

/** purchase.salefish.app: the buyers' and partners' pages only (shared/purchaseHost.ts). */
const purchase = isPurchaseHost(window.location.hostname);

function boot() {
  // Prevent browser scroll restoration on navigation (we handle it manually)
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  /*
    Catch the install offer before anything renders.

    Chromium fires `beforeinstallprompt` once, early, and it is gone unless
    something calls preventDefault() on it — usually before React has mounted.
    It used to be caught inside PwaInstallDialog's effect, which only runs once
    the account menu is opened, so the one-tap Install button almost never
    appeared. Caught here and parked; see lib/pwa.ts.
  */
  // Not on the purchase host: nobody installs a page they open once from an email.
  if (!purchase) captureInstallPrompt();

  /*
    Give the iOS back swipe somewhere to go before anything renders.

    In the installed app the edge swipe is the only way back, and at the bottom
    of the history stack it leaves the document for a blank white screen. This
    seeds one entry under the session so it cannot. Before the mount, so the two
    history writes happen with nothing subscribed and its popstate listener runs
    ahead of wouter's. A no-op anywhere but an installed iOS app; see
    lib/backGuard.ts.
  */
  installBackGuard();

  /*
    Google Analytics — a no-op unless VITE_GA_MEASUREMENT_ID is configured, and
    never on a buyer's quote or registration page. The tag itself is fetched
    after `load`, in idle time; commands queue until it arrives. See
    lib/analytics.ts.
  */
  initAnalytics();

  /*
    The browser's bars follow the theme the reader chose, not only the device's
    setting — see lib/themeColor.ts.
  */
  installThemeColorSync();

  // Room for iPadOS 26's window controls when the app runs in a window — the
  // one top inset the device does not report. See lib/windowChrome.ts.
  initWindowChrome();

  // The flat shell's ground, menu state and project colour, before the first
  // paint, so the cold launch's frame is the one AppShell will draw.
  if (!purchase) {
    applyShellAtBoot(window.location.pathname);
    watchHardwareKeyboard();
  }

  /*
    Decode the project logos and covers the opening screen will draw, while the
    session and the projects list are still being fetched — so they paint with
    the page instead of after it. Paths remembered from the last load; see
    lib/projectImages.ts.
  */
  if (!purchase) warmProjectImagesAtBoot();

  createRoot(document.getElementById("root")!).render(<IconStroke><App /></IconStroke>);

  // Registration lives in its own module because it has a lifecycle, not just
  // a call: the worker parks in `waiting` instead of taking over immediately,
  // and something has to offer it, or apply it on a resume. No worker in
  // `npm run dev` unless VITE_SW_DEV=1. See lib/serviceWorkerRegistration.ts.
  // None on the purchase host: a buyer's signing link must never be answered
  // from a stored copy of the staff app.
  if (!purchase) registerServiceWorker();
}

/*
  Console inside a rep's Showroom screen (embed/EmbedApp.tsx;
  shared/showroomEmbed.ts): its own small root, and none of the app's
  start-up — no install offer, back guard, analytics, theme sync, window
  chrome, warmed images or service worker, none of which belong in a frame.
*/
const embedded = window.location.pathname.startsWith("/embed/");

if (purchase && !embedded && !isBuyerPath(window.location.pathname)) {
  // The host itself shows where documents are opened from; any staff page goes to Console.
  const { pathname, search, hash } = window.location;
  if (pathname === "/" || pathname === "") {
    window.history.replaceState(null, "", PURCHASE_HOME_PATH);
    boot();
  } else {
    window.location.replace(`${consoleOriginFor(window.location.origin)}${pathname}${search}${hash}`);
  }
} else if (embedded) {
  void import("./embed/EmbedApp").then(({ default: EmbedApp }) => {
    createRoot(document.getElementById("root")!).render(<IconStroke><EmbedApp /></IconStroke>);
  });
} else {
  boot();
}
